import { useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import type {
  PremiseConfig,
  SchemeContent,
  SchemeKnobValues,
  SchemeManifest,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { hasPromotionsLicense } from "@shared/products";
import LivePreview from "../../components/template/LivePreview";
import TemplateSubEditor, {
  useTemplateApps,
} from "../../components/template/TemplateSubEditor";
import {
  buildScreenGroups,
  moveZone,
  resolveOrders,
  type ScreenGroup,
  type ScreenRow,
} from "../../components/template/screensModel";
import { PREVIEW_SCREEN } from "../../components/template/TemplateEditor";
import {
  ActiveChoice,
  FieldBlock,
  InfoCallout,
  PanelHeading,
  SectionList,
  SegmentedChoice,
  StartNavLayout,
  type Crumb,
  type ListGroup,
  type ListItem,
} from "../../components/startnav/StartNavLayout";
import { tokens } from "../../theme";
import {
  AppsPanel,
  RadioPanel,
} from "../../components/startnav/CataloguePanels";

/**
 * The home-screen tiles every template is sold with. Listed first, as
 * "Standard sections", and everything else a template can show follows under
 * "More sections" (Sigge, 2026-09-29). All of them keep their on/off where the
 * template has one - this is a grouping, not a change to what can be switched.
 */
const STANDARD_SCREENS = ["cast", "apps", "info", "radio"];

const HOME_ZONE = "Home screen";
const INFO_ZONE = "Inside Information";

const HOME_SUBTITLE =
  "Choose which menu items show on the home screen, and in what order.";

export const START_NAV_ROOT: Crumb = {
  label: "Start & navigation",
  to: "/frontlayer/home",
};

/** The screen a row stands for. Rows built from an order knob are keyed by the
 *  screen id already; the rest are keyed by their toggle knob. */
function screenIdOf(manifest: SchemeManifest, row: ScreenRow): string {
  const knob = manifest.knobs.find((k) => k.id === row.key);
  if (knob?.type === "toggle" && knob.controls?.kind === "screen") {
    return knob.controls.id;
  }
  return row.key;
}

/**
 * Put `subset` back into `full` in its new order, leaving every other id where
 * it was. A list group shows only part of an order knob (Standard vs More), so
 * a drag inside one group must not disturb the rows of the other.
 */
export function reorderSubset(full: string[], subset: string[]): string[] {
  const members = new Set(subset);
  const queue = subset.slice();
  return full.map((id) => (members.has(id) ? (queue.shift() ?? id) : id));
}

/**
 * Start & navigation → Home screen ("Startvy").
 *
 * Left: what the guest's home screen offers, in order, each with its state.
 * Middle: the selected section's switch and content. Right: the TV, opened on
 * that section.
 *
 * Information is the one section with sections of its own (Wi-Fi, Weather ...).
 * They nest under it in the SAME list when it is opened - an earlier version
 * swapped the whole list for Information's, and every other section vanished
 * (Sigge, 2026-09-29). The breadcrumb gains "Information" while you are in it.
 */
export default function HomeScreenPage({
  premise,
  onChange,
}: {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}) {
  const [params, setParams] = useSearchParams();
  const apps = useTemplateApps(premise);
  const binding = premise.scheme;
  const manifest = binding ? SCHEME_MANIFESTS[binding.key] : undefined;

  const groups = useMemo(
    () =>
      manifest
        ? buildScreenGroups(manifest, binding?.knobs, {
            hasPromotions: hasPromotionsLicense(premise.products),
            appsCertified: apps.certifiedOn.size > 0,
            certificationNote: apps.certNote,
          })
        : [],
    [
      manifest,
      binding?.knobs,
      premise.products,
      apps.certifiedOn.size,
      apps.certNote,
    ]
  );

  if (!binding || !manifest) {
    return (
      <StartNavLayout
        crumbs={[START_NAV_ROOT, { label: "Home screen" }]}
        pageTitle="Home screen"
        pageSubtitle={HOME_SUBTITLE}
        title="Home screen"
        list={null}
        editor={
          <Typography variant="body2" color="text.secondary">
            This place has no template yet. Pick one under{" "}
            <RouterLink to="/frontlayer/appearance">
              Appearance → Pick template
            </RouterLink>
            , then come back to arrange its home screen.
          </Typography>
        }
      />
    );
  }

  const knobs = binding.knobs ?? {};
  const setKnobs = (next: SchemeKnobValues) =>
    onChange({ ...premise, scheme: { ...binding, knobs: next } });
  const setContent = (content: SchemeContent) =>
    onChange({ ...premise, scheme: { ...binding, content } });

  const zone = (title: string): ScreenGroup | undefined =>
    groups.find((g) => g.title === title);
  const homeZone = zone(HOME_ZONE);
  const infoZone = zone(INFO_ZONE);
  const homeRows = homeZone?.rows ?? [];
  const infoRows = infoZone?.rows ?? [];

  const reorderIn =
    (group: ScreenGroup | undefined) =>
    (keys: string[]): void => {
      if (!group?.orderKnobId) return;
      const full = resolveOrders(manifest, knobs)[group.orderKnobId] ?? [];
      setKnobs({ ...knobs, [group.orderKnobId]: reorderSubset(full, keys) });
    };

  const toItem = (r: ScreenRow): ListItem => {
    // The screen id, not the row's knob id: it is what `?item=` carries, so a
    // link reads /frontlayer/home?item=wifi on every template.
    const key = screenIdOf(manifest, r);
    return {
      key,
      label: r.label,
      on: r.fixedNote ? true : r.on,
      orderable: r.orderable,
      ...(key === "info" && infoRows.length
        ? {
            children: infoRows.map(toItem),
            onReorderChildren: reorderIn(infoZone),
          }
        : {}),
    };
  };

  const standard = homeRows.filter((r) =>
    STANDARD_SCREENS.includes(screenIdOf(manifest, r))
  );
  const more = homeRows.filter((r) => !standard.includes(r));
  const listGroups: ListGroup[] = [
    {
      key: "standard",
      title: "Standard sections",
      subtitle: "Choose a section to configure its content and settings.",
      items: standard.map(toItem),
      onReorder: reorderIn(homeZone),
    },
    ...(more.length
      ? [
          {
            key: "more",
            title: "More sections",
            subtitle: "Extra sections you can switch on for this place.",
            items: more.map(toItem),
            onReorder: reorderIn(homeZone),
          },
        ]
      : []),
  ];

  const allRows = [...homeRows, ...infoRows];
  const idOf = (r: ScreenRow) => screenIdOf(manifest, r);
  const requested = params.get("item") ?? "";
  const selectedRow =
    allRows.find((r) => idOf(r) === requested) ?? standard[0] ?? homeRows[0];
  const selectedKey = selectedRow ? idOf(selectedRow) : undefined;
  const insideInfo = !!selectedRow && infoRows.includes(selectedRow);
  const inInformation = insideInfo || selectedKey === "info";

  const select = (key: string) =>
    setParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        q.set("item", key);
        return q;
      },
      { replace: true }
    );

  const label = selectedRow?.label ?? "";
  const editor = selectedRow ? (
    <RowEditor
      key={selectedKey}
      premise={premise}
      manifest={manifest}
      knobs={knobs}
      row={selectedRow}
      label={label}
      sub={selectedRow.sub}
      screenId={selectedKey}
      isHomeView={!insideInfo}
      onKnobs={setKnobs}
      onContent={setContent}
      onChange={onChange}
      apps={apps}
    />
  ) : (
    <Typography variant="body2" color="text.secondary">
      This template has nothing to arrange here.
    </Typography>
  );

  const previewScreen =
    (selectedRow?.sub && PREVIEW_SCREEN[selectedRow.sub]) ||
    (selectedKey && selectedKey !== "cast" ? selectedKey : undefined);

  return (
    <StartNavLayout
      crumbs={
        inInformation
          ? [
              START_NAV_ROOT,
              { label: "Home screen", to: "/frontlayer/home" },
              { label: "Information" },
            ]
          : [START_NAV_ROOT, { label: "Home screen" }]
      }
      pageTitle="Home screen"
      pageSubtitle={HOME_SUBTITLE}
      title={inInformation ? "Information" : "Menu items"}
      subtitle={
        inInformation
          ? "Edit the content shown on the Information page of the home screen."
          : "Pick a section to change its content and settings. Drag to reorder."
      }
      list={
        <SectionList
          groups={listGroups}
          selected={selectedKey}
          onSelect={select}
        />
      }
      editor={editor}
      preview={
        <Box>
          <PanelHeading
            title="Preview"
            subtitle={`How ${label || "the home screen"} looks on the screen.`}
          />
          <LivePreview
            premise={premise}
            screen={previewScreen}
            maxWidth={440}
            hideLabel
          />
        </Box>
      }
    />
  );
}

/** The middle column for one section. */
function RowEditor({
  premise,
  manifest,
  knobs,
  row,
  label,
  sub,
  screenId,
  isHomeView,
  onKnobs,
  onContent,
  onChange,
  apps,
}: {
  premise: PremiseConfig;
  manifest: SchemeManifest;
  knobs: SchemeKnobValues;
  row: ScreenRow;
  label: string;
  sub: ScreenRow["sub"];
  screenId?: string;
  isHomeView: boolean;
  onKnobs: (next: SchemeKnobValues) => void;
  onContent: (next: SchemeContent) => void;
  onChange: (next: PremiseConfig) => void;
  apps: ReturnType<typeof useTemplateApps>;
}) {
  const content = premise.scheme!.content;
  const setOn = row.knobId
    ? (on: boolean) => onKnobs({ ...knobs, [row.knobId!]: on })
    : undefined;
  const where = isHomeView ? "on the home screen" : "on the Information page";

  // Where the section's CONTENT is edited, when it is not edited here.
  const elsewhere =
    row.key === "showPromotions" || sub === "slots"
      ? { to: "/promotions/slots", label: "Edit under Promotions" }
      : screenId === "offers"
        ? { to: "/promotions/offers", label: "Edit under Promotions → Offers" }
        : undefined;

  return (
    <Box data-testid={`startnav-editor-${screenId ?? row.key}`}>
      <PanelHeading
        title={`Manage ${label}`}
        subtitle={`Configure ${label}.`}
      />

      <ActiveChoice
        label={label}
        title={
          isHomeView
            ? "Shown on the home screen"
            : "Shown on the Information page"
        }
        on={row.on}
        onChange={setOn}
        fixedNote={row.fixedNote}
        help={`Controls whether ${label} is shown ${where}. Its settings are kept while it is hidden.`}
      />

      {/* A screen the operator names. Custom carries its own name field. */}
      {row.title && sub !== "custom" && (
        <FieldBlock label="Name" help={row.title.help}>
          <TextField
            size="small"
            fullWidth
            value={row.title.raw}
            placeholder={row.title.placeholder}
            inputProps={{ maxLength: row.title.maxLength }}
            onChange={(e) =>
              onKnobs({ ...knobs, [row.title!.knobId]: e.target.value })
            }
            data-testid="startnav-title-field"
          />
        </FieldBlock>
      )}

      {row.moveTo && row.orderKnobId && (
        <FieldBlock
          label="Placement"
          help="Show it as a tile on the home screen, or as a section inside Information."
        >
          <SegmentedChoice
            value={isHomeView ? "home" : "info"}
            options={[
              { value: "home", label: "Home screen" },
              { value: "info", label: "Inside Information" },
            ]}
            onChange={() => {
              const patch = moveZone(
                manifest,
                knobs,
                row.key,
                row.moveTo!.orderKnobId
              );
              if (patch) onKnobs(patch);
            }}
            testId="startnav-placement"
          />
        </FieldBlock>
      )}

      {elsewhere ? (
        <Button
          component={RouterLink}
          to={elsewhere.to}
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          data-testid="startnav-edit-elsewhere"
        >
          {elsewhere.label}
        </Button>
      ) : sub === "apps" ? (
        <AppsPanel
          content={content}
          supportedLocales={premise.supportedLocales}
          defaultLocale={premise.defaultLocale}
          global={apps.apps}
          isCare={apps.isCare}
          onChange={onContent}
        />
      ) : sub === "radio" ? (
        <RadioPanel
          content={content}
          supportedLocales={premise.supportedLocales}
          defaultLocale={premise.defaultLocale}
          onChange={onContent}
        />
      ) : sub === "wifi" ? (
        <WifiFields content={content} onChange={onContent} />
      ) : sub === "weather" ? (
        <WeatherFields content={content} onChange={onContent} />
      ) : sub ? (
        <TemplateSubEditor
          premise={premise}
          sub={sub}
          onChange={onChange}
          apps={apps}
          stateBar={false}
        />
      ) : (
        row.help && (
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
            {row.help}
          </Typography>
        )
      )}
    </Box>
  );
}

/** Wi-Fi, laid out as the design draws it (frame 1748:20043). */
function WifiFields({
  content,
  onChange,
}: {
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
}) {
  const wifi = content.wifi ?? { ssid: "" };
  const set = (part: Partial<NonNullable<SchemeContent["wifi"]>>) =>
    onChange({ ...content, wifi: { ...wifi, ...part } });
  const [show, setShow] = useState(false);
  return (
    <>
      <FieldBlock label="Network name (SSID)">
        <TextField
          size="small"
          fullWidth
          placeholder="Network name (SSID)"
          value={wifi.ssid}
          onChange={(e) => set({ ssid: e.target.value })}
          data-testid="wifi-ssid"
        />
      </FieldBlock>
      <FieldBlock label="Password">
        <TextField
          size="small"
          fullWidth
          placeholder="Password"
          type={show ? "text" : "password"}
          value={wifi.password ?? ""}
          onChange={(e) => set({ password: e.target.value || undefined })}
          data-testid="wifi-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={show ? "Hide password" : "Show password"}
                  onClick={() => setShow((s) => !s)}
                  edge="end"
                >
                  {show ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FieldBlock>
      <FieldBlock
        label="Show QR code"
        help="Shows a QR code guests can scan to join."
      >
        <SegmentedChoice
          value={wifi.showQr ? "yes" : "no"}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
          onChange={(v) => set({ showQr: v === "yes" })}
          testId="wifi-qr"
        />
      </FieldBlock>
    </>
  );
}

/** Weather, laid out as the design draws it (frame 1756:21275). */
function WeatherFields({
  content,
  onChange,
}: {
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
}) {
  const w = content.weather ?? {};
  return (
    <>
      <FieldBlock label="Unit" help="Temperatures on the Weather section.">
        <SegmentedChoice
          value={w.units === "f" ? "f" : "c"}
          options={[
            { value: "c", label: "Celsius" },
            { value: "f", label: "Fahrenheit" },
          ]}
          onChange={(units) =>
            onChange({ ...content, weather: { ...w, units } })
          }
          testId="weather-units"
        />
      </FieldBlock>
      <InfoCallout title="Location for weather data">
        The forecast is fetched from the saved location of the place. If the
        place has no coordinates, the Weather section is not shown on the
        screen.
      </InfoCallout>
    </>
  );
}
